import { Box } from '@tempmee/react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { CenteredSpinner, Pagination } from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import type { TShiftCardData } from '~/types/ShiftCard';
import type { TFilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../hooks';
import { EmptyMessage } from './EmptyMessage';
import { ShiftOverviewCard } from './ShiftOverviewCard';
import { ShiftOverviewList } from './ShiftOverviewList';
import type { IInviteConfig } from './useShiftOverviewCard';

interface IShiftOverviewTabProps {
  jobType: DashboardJobsType;
  /** Dashboard list status to query for this tab. */
  listStatus: DashboardJobsStatus;
  /** Lifecycle status passed to every card. */
  cardStatus: TShiftCardData['status'];
  dataCy: string;
  emptyType: TFilterStatusEnum;
  /** Route opened when a card is clicked. */
  cardClickTo: (item: IDashboardJob) => string;
  /** When set, adds an "Edit Shift/Interview" overflow item routing here. */
  editTo?: (item: IDashboardJob) => string;
  /** When set, adds a "Message professional" overflow item routing here. */
  chatTo?: (item: IDashboardJob) => string;
  invite?: IInviteConfig;
  invitePlacement?: 'envelope' | 'menu';
  canCancel?: boolean;
}

/**
 * Shared Shift Overview tab: queries the dashboard list for a status and renders the
 * paginated DS shift cards. Navigation is declarative (route builders) so the concrete
 * Open/Scheduled tabs stay as thin config wrappers with no duplicated boilerplate.
 */
export const ShiftOverviewTab = ({
  jobType,
  listStatus,
  cardStatus,
  dataCy,
  emptyType,
  cardClickTo,
  editTo,
  chatTo,
  invite,
  invitePlacement,
  canCancel,
}: IShiftOverviewTabProps) => {
  const navigate = useNavigate();

  const { data, isInitialLoading, isLoading, methods, pagination } = useJobList({
    status: listStatus,
    jobType,
  });

  const handleCardClick = useCallback(
    (item: IDashboardJob) => navigate(cardClickTo(item)),
    [navigate, cardClickTo],
  );
  const handleEdit = useCallback(
    (item: IDashboardJob) => {
      if (editTo) navigate(editTo(item));
    },
    [navigate, editTo],
  );
  const handleChat = useCallback(
    (item: IDashboardJob) => {
      if (chatTo) navigate(chatTo(item));
    },
    [navigate, chatTo],
  );

  if (isInitialLoading) {
    return (
      <Box pt={8}>
        <CenteredSpinner />
      </Box>
    );
  }

  return (
    <ShiftOverviewList
      dataCy={dataCy}
      isEmpty={data.length === 0}
      isLoading={isLoading}
      EmptyComponent={<EmptyMessage type={emptyType} />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={methods.setCurrentPage}
          maxButtons={5}
        />
      }
    >
      {data.map((item) => (
        <ShiftOverviewCard
          key={item.id}
          item={item}
          status={cardStatus}
          onCardClick={handleCardClick}
          invite={invite}
          invitePlacement={invitePlacement}
          onEdit={editTo ? handleEdit : undefined}
          onChat={chatTo ? handleChat : undefined}
          canCancel={canCancel}
        />
      ))}
    </ShiftOverviewList>
  );
};
